import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Wifi, 
  Calendar, 
  Gauge, 
  Bell, 
  Check,
  Shield,
  Zap
} from 'lucide-react';

const ProductPage = () => {
  const { t } = useLanguage();

  const specs = [
    { label: 'Connection / 連接方式', value: '4G/LTE DTU' },
    { label: 'Power / 電源', value: '12V DC / Solar' },
    { label: 'Valve Size / 閥門尺寸', value: '1" - 4"' },
    { label: 'Pressure Range / 壓力範圍', value: '0.5 - 5 bar' },
    { label: 'IP Rating / 防護等級', value: 'IP67' },
    { label: 'Temperature / 工作溫度', value: '-10°C to 60°C' },
  ];

  const benefits = [
    t('product_benefit_1'),
    t('product_benefit_2'),
    t('product_benefit_3'),
    t('product_benefit_4'),
  ];

  return (
    <div className="min-h-screen bg-fac-dark pt-16">
      {/* Hero */}
      <section className="py-20 relative overflow-hidden" data-testid="product-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=1920&q=80"
            alt="Irrigation system"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-fac-dark via-fac-dark/80 to-fac-dark" />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading font-black text-5xl md:text-6xl text-white tracking-tighter uppercase mb-4">
                {t('product_title')}
              </h1>
              <p className="text-2xl text-fac-green font-chinese mb-6">
                {t('product_subtitle')}
              </p>
              <p className="text-lg text-gray-300 mb-8 font-chinese">
                Control your irrigation remotely with our smart valve system. 
                Built for reliability and ease of use.
                <br /><br />
                使用我們的智慧閥門系統遠端控制灌溉。
                專為可靠性和易用性而設計。
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-fac-green flex-shrink-0" />
                    <span className="text-white font-chinese">{benefit}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button 
                  size="lg"
                  className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim h-14 px-8"
                  data-testid="product-contact-btn"
                >
                  <span>GET A QUOTE / 取得報價</span>
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="cyber-border p-8 bg-fac-card/50 backdrop-blur-sm rounded-lg">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80"
                  alt="FAC Smart Valve"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-fac-paper" data-testid="product-features">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading font-black text-4xl text-white tracking-tighter uppercase text-center mb-12">
            KEY FEATURES / 主要功能
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-fac-card border-border border-l-4 border-l-fac-green">
              <CardContent className="p-6">
                <Wifi className="w-10 h-10 text-fac-green mb-4" />
                <h3 className="font-heading font-bold text-xl text-white uppercase mb-2">
                  Remote Open / Close
                </h3>
                <p className="text-muted-foreground font-chinese">
                  Open or close your valve from anywhere. Just tap the button on your phone.
                  <br />
                  隨時隨地開關閥門。只需在手機上點擊按鈕。
                </p>
              </CardContent>
            </Card>

            <Card className="bg-fac-card border-border border-l-4 border-l-fac-green">
              <CardContent className="p-6">
                <Calendar className="w-10 h-10 text-fac-green mb-4" />
                <h3 className="font-heading font-bold text-xl text-white uppercase mb-2">
                  Schedule Irrigation
                </h3>
                <p className="text-muted-foreground font-chinese">
                  Set start time, duration, and repeat patterns. The system handles the rest.
                  <br />
                  設定開始時間、持續時間和重複模式。系統自動執行。
                </p>
              </CardContent>
            </Card>

            <Card className="bg-fac-card border-border border-l-4 border-l-fac-green">
              <CardContent className="p-6">
                <Gauge className="w-10 h-10 text-fac-green mb-4" />
                <h3 className="font-heading font-bold text-xl text-white uppercase mb-2">
                  Pressure Control
                </h3>
                <p className="text-muted-foreground font-chinese">
                  Adjust water pressure for optimal irrigation. Monitor in real-time.
                  <br />
                  調整水壓以達最佳灌溉效果。即時監控。
                </p>
              </CardContent>
            </Card>

            <Card className="bg-fac-card border-border border-l-4 border-l-fac-green">
              <CardContent className="p-6">
                <Bell className="w-10 h-10 text-fac-green mb-4" />
                <h3 className="font-heading font-bold text-xl text-white uppercase mb-2">
                  Instant Alerts
                </h3>
                <p className="text-muted-foreground font-chinese">
                  Get notified when something needs attention. Device offline, pressure issues, etc.
                  <br />
                  當需要注意時立即通知。設備離線、壓力問題等。
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-20 bg-fac-dark" data-testid="product-specs">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h2 className="font-heading font-black text-4xl text-white tracking-tighter uppercase text-center mb-12">
            SPECIFICATIONS / 規格
          </h2>

          <div className="bg-fac-card border border-border rounded-lg overflow-hidden">
            {specs.map((spec, index) => (
              <div 
                key={index}
                className={`flex justify-between items-center p-4 ${
                  index !== specs.length - 1 ? 'border-b border-border' : ''
                }`}
              >
                <span className="text-muted-foreground font-chinese">{spec.label}</span>
                <span className="text-white font-bold">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
